// LabRatGifts Custom Shopify Theme - JavaScript

class LabRatGiftsTheme {
  constructor() {
    this.init();
  }

  init() {
    // CRITICAL FIX: Move mobile drawer outside Shopify section wrapper to escape stacking context
    this.relocateMobileDrawer();
    
    this.initStickyHeader();
    this.initQuantityInputs();
    this.initProductForms();
    this.initBuyNowButtons();
    this.initProductDetailController();
    this.initAnnouncementSlider();
    this.initMegaMenu();
    this.initMobileMenu();
    this.initResponsiveNavigation();
    this.initSearchBar();
    this.initSearchModal();
    this.initAccountModal();
    this.initModernCartDrawer();
    this.initQuickView();
    this.initCompare();
    this.initWishlist();
    this.initProductGallery();
    this.initFBT();
  }

  relocateMobileDrawer() {
    const mobileMenu = document.getElementById('mobile-menu');
    if (mobileMenu && mobileMenu.parentElement !== document.body) {
      document.body.appendChild(mobileMenu);
    }
  }

  // CRITICAL: Dynamic sticky header positioning for 30M+ users
  initStickyHeader() {
    const announcementBar = document.querySelector('.labratgifts-announcement-bar');
    const header = document.querySelector('.labratgifts-header');
    
    const updateHeaderOffset = () => {
      // Calculate announcement bar height (only if visible in viewport)
      let announcementHeight = 0;
      if (announcementBar) {
        const announcementRect = announcementBar.getBoundingClientRect();
        if (announcementRect.bottom > 0) {
          announcementHeight = announcementBar.offsetHeight;
        }
      }
      document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
      
      // Calculate header height
      const headerHeight = header ? header.offsetHeight : 80;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    };

    // Update on load
    updateHeaderOffset();
    
    // Also update after a short delay for dynamic content
    setTimeout(updateHeaderOffset, 100);

    // Update on scroll with requestAnimationFrame for smooth performance
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateHeaderOffset();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Update on window resize (for responsive changes)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateHeaderOffset, 50);
    });

    // Update on DOM changes (for dynamic content)
    if ('ResizeObserver' in window) {
      if (announcementBar) {
        const announcementObserver = new ResizeObserver(updateHeaderOffset);
        announcementObserver.observe(announcementBar);
      }
      
      if (header) {
        const headerObserver = new ResizeObserver(updateHeaderOffset);
        headerObserver.observe(header);
      }
    }
  }

  // Product Description Layout - Tabs & Accordion
  initProductDescriptionLayout() {
    // Initialize Tabs
    try {
      const tabsContainers = document.querySelectorAll('[data-product-tabs]');
      if (typeof ProductDescriptionTabs !== 'undefined') {
        tabsContainers.forEach(container => {
          new ProductDescriptionTabs(container);
        });
      }
    } catch (error) {
      console.error('Error initializing product tabs:', error);
    }
    
    // Initialize Accordions
    try {
      const accordionContainers = document.querySelectorAll('[data-accordion]');
      if (typeof ProductDescriptionAccordion !== 'undefined') {
        accordionContainers.forEach(container => {
          new ProductDescriptionAccordion(container);
        });
      }
    } catch (error) {
      console.error('Error initializing product accordions:', error);
    }
  }

  initSearchBar() {
    const searchToggle = document.querySelector('.labratgifts-search-toggle');
    const searchBar = document.getElementById('search-bar');
    const searchClose = document.querySelector('.labratgifts-search-close');
    const searchInput = document.querySelector('.labratgifts-search-input');
    
    const closeSearchBar = () => {
      if (searchBar && searchToggle) {
        searchBar.setAttribute('hidden', '');
        searchToggle.setAttribute('aria-expanded', 'false');
        searchToggle.focus();
      }
    };
    
    if (searchToggle && searchBar) {
      searchToggle.addEventListener('click', () => {
        const isHidden = searchBar.hasAttribute('hidden');
        if (isHidden) {
          searchBar.removeAttribute('hidden');
          searchToggle.setAttribute('aria-expanded', 'true');
          if (searchInput) searchInput.focus();
        } else {
          closeSearchBar();
        }
      });
      
      if (searchClose) {
        searchClose.addEventListener('click', closeSearchBar);
      }
      
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !searchBar.hasAttribute('hidden')) {
          closeSearchBar();
        }
      });
    }
  }

  initAnnouncementSlider() {
    const sliders = document.querySelectorAll('.labratgifts-announcement-slider');
    
    sliders.forEach(slider => {
      const slides = slider.querySelectorAll('.labratgifts-announcement-slide');
      const prevBtn = slider.closest('.labratgifts-announcement-bar')?.querySelector('.labratgifts-announcement-prev');
      const nextBtn = slider.closest('.labratgifts-announcement-bar')?.querySelector('.labratgifts-announcement-next');
      let currentSlide = 0;
      let autoplayInterval;
      
      // Exit early if no slides at all
      if (slides.length === 0) return;
      
      function showSlide(index) {
        slides.forEach(slide => slide.classList.remove('active'));
        if (slides[index]) slides[index].classList.add('active');
      }
      
      // Always show first slide immediately
      showSlide(0);
      
      // Only setup rotation if multiple slides exist
      if (slides.length > 1) {
        function nextSlide() {
          currentSlide = (currentSlide + 1) % slides.length;
          showSlide(currentSlide);
        }
        
        function prevSlide() {
          currentSlide = (currentSlide - 1 + slides.length) % slides.length;
          showSlide(currentSlide);
        }
        
        if (slider.dataset.autoplay === 'true') {
          const interval = parseInt(slider.dataset.interval) || 5000;
          autoplayInterval = setInterval(nextSlide, interval);
          
          slider.addEventListener('mouseenter', () => clearInterval(autoplayInterval));
          slider.addEventListener('mouseleave', () => {
            autoplayInterval = setInterval(nextSlide, interval);
          });
        }
        
        if (nextBtn) nextBtn.addEventListener('click', nextSlide);
        if (prevBtn) prevBtn.addEventListener('click', prevSlide);
      }
    });
  }

  initMegaMenu() {
    const navItems = document.querySelectorAll('.labratgifts-nav-item');
    
    navItems.forEach(item => {
      const megaMenu = item.querySelector('.labratgifts-mega-menu');
      const dropdown = item.querySelector('.labratgifts-nav-dropdown');
      let enterTimeoutId;
      let leaveTimeoutId;
      
      if (megaMenu || dropdown) {
        const showMenu = () => {
          clearTimeout(leaveTimeoutId);
          clearTimeout(enterTimeoutId);
          enterTimeoutId = setTimeout(() => {
            item.classList.add('active');
          }, 50);
        };
        
        const hideMenu = () => {
          clearTimeout(enterTimeoutId);
          clearTimeout(leaveTimeoutId);
          leaveTimeoutId = setTimeout(() => {
            item.classList.remove('active');
          }, 350);
        };
        
        item.addEventListener('mouseenter', showMenu);
        item.addEventListener('mouseleave', hideMenu);
        
        if (megaMenu) {
          megaMenu.addEventListener('mouseenter', () => {
            clearTimeout(leaveTimeoutId);
            clearTimeout(enterTimeoutId);
            item.classList.add('active');
          });
          megaMenu.addEventListener('mouseleave', hideMenu);
        }
        
        if (dropdown) {
          dropdown.addEventListener('mouseenter', () => {
            clearTimeout(leaveTimeoutId);
            clearTimeout(enterTimeoutId);
            item.classList.add('active');
          });
          dropdown.addEventListener('mouseleave', hideMenu);
        }
        
        const link = item.querySelector('.labratgifts-nav-link');
        if (link) {
          link.addEventListener('focus', () => {
            clearTimeout(leaveTimeoutId);
            item.classList.add('active');
          });
          link.addEventListener('blur', () => {
            setTimeout(() => {
              if (!item.contains(document.activeElement)) {
                item.classList.remove('active');
              }
            }, 150);
          });
        }
      }
    });
  }

  initMobileMenu() {
    const menuToggles = document.querySelectorAll('.labratgifts-menu-toggle');
    const mobileMenu = document.querySelector('.labratgifts-mobile-menu');
    const mobileClose = document.querySelector('.labratgifts-mobile-menu__close');
    const mobileOverlay = document.querySelector('.labratgifts-mobile-menu__overlay');
    
    if (mobileMenu) {
      const openMobileMenu = () => {
        mobileMenu.classList.add('active');
        document.body.style.overflow = 'hidden';
        menuToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'true');
        });
      };
      
      const closeMobileMenu = () => {
        mobileMenu.classList.remove('active');
        document.body.style.overflow = '';
        menuToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
        });
      };
      
      menuToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = mobileMenu.classList.contains('active');
          if (isOpen) {
            closeMobileMenu();
          } else {
            openMobileMenu();
          }
        });
      });
      
      if (mobileClose) mobileClose.addEventListener('click', closeMobileMenu);
      if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileMenu);
      
      const mobileItems = mobileMenu.querySelectorAll('.labratgifts-mobile-menu-item');
      mobileItems.forEach(item => {
        const toggle = item.querySelector('.labratgifts-mobile-submenu-toggle');
        if (toggle) {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            item.classList.toggle('active');
          });
        }
      });
    }
  }

  initResponsiveNavigation() {
    const header = document.querySelector('.labratgifts-header');
    if (!header) return;
    
    const collapseMode = header.getAttribute('data-nav-collapse-mode');
    if (collapseMode !== 'auto') return; // Only run for auto mode
    
    const nav = header.querySelector('.labratgifts-header__nav');
    const wrapper = header.querySelector('.labratgifts-header__wrapper');
    const icons = header.querySelector('.labratgifts-header__icons');
    const logo = header.querySelector('.labratgifts-header__logo');
    const navList = nav ? nav.querySelector('.labratgifts-nav-list') : null;
    
    if (!nav || !wrapper || !navList) return;
    
    const checkNavFit = () => {
      // Skip check on mobile (always collapsed)
      if (window.innerWidth < 768) {
        header.classList.add('nav-collapsed');
        header.classList.remove('nav-fits');
        return;
      }
      
      // CRITICAL FIX: Temporarily force expanded state for accurate measurement
      // Otherwise hamburger button width is included in icons, and nav links report 0 width
      const wasCollapsed = header.classList.contains('nav-collapsed');
      header.classList.remove('nav-collapsed');
      header.classList.add('nav-fits');
      
      // Force browser reflow to get accurate measurements
      void wrapper.offsetHeight;
      
      // Calculate available space for navigation
      const wrapperWidth = wrapper.offsetWidth;
      const logoWidth = logo ? logo.offsetWidth : 0;
      
      // Measure icons WITHOUT hamburger button (it's hidden in expanded state)
      const iconsWidth = icons ? icons.offsetWidth : 0;
      
      // PRODUCTION FIX: Accurate width measurement using computed styles
      const navLinks = nav.querySelectorAll('.labratgifts-nav-list > .labratgifts-nav-item > .labratgifts-nav-link');
      
      // Get the actual computed gap from CSS (not a hardcoded estimate)
      const computedStyle = window.getComputedStyle(navList);
      const gap = parseFloat(computedStyle.gap || computedStyle.columnGap || '0');
      
      // Sum actual link widths (now they're visible so offsetWidth is accurate)
      let navWidth = 0;
      navLinks.forEach(link => {
        navWidth += link.offsetWidth;
      });
      
      // Add actual gaps between items
      if (navLinks.length > 1) {
        navWidth += gap * (navLinks.length - 1);
      }
      
      // Add buffer for padding/margins (20px for safety)
      const availableSpace = wrapperWidth - logoWidth - iconsWidth - 20;
      
      // Now apply the correct state based on accurate measurements
      if (navWidth > availableSpace) {
        header.classList.add('nav-collapsed');
        header.classList.remove('nav-fits');
      } else {
        header.classList.remove('nav-collapsed');
        header.classList.add('nav-fits');
      }
    };
    
    // CRITICAL FIX: Initialize with nav-fits class to prevent flash of hamburger on desktop
    // Then immediately run checkNavFit to apply correct state
    header.classList.add('nav-fits');
    header.classList.remove('nav-collapsed');
    
    // Check on load (run after a short delay to ensure DOM is ready)
    requestAnimationFrame(() => {
      checkNavFit();
    });
    
    // Check on resize with debounce
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkNavFit, 150);
    });
    
    // Recheck when fonts load (can affect width)
    if (document.fonts) {
      document.fonts.ready.then(checkNavFit);
    }
    
    // Use ResizeObserver for dynamic updates when cart count or icons change
    if ('ResizeObserver' in window) {
      const iconsObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(checkNavFit, 100);
      });
      
      if (icons) iconsObserver.observe(icons);
      if (logo) iconsObserver.observe(logo);
    }
  }

  initCartDrawer() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const cartDrawer = document.getElementById('cartDrawer');
    const cartClose = document.querySelector('.labratgifts-cart-drawer__close');
    const cartOverlay = document.querySelector('.labratgifts-cart-drawer__overlay');

    if (cartIcon && cartDrawer) {
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        cartDrawer.classList.add('active');
      });

      if (cartClose) {
        cartClose.addEventListener('click', () => {
          cartDrawer.classList.remove('active');
        });
      }

      if (cartOverlay) {
        cartOverlay.addEventListener('click', () => {
          cartDrawer.classList.remove('active');
        });
      }
    }
  }

  initQuantityInputs() {
    const quantityInputs = document.querySelectorAll('[data-quantity-input]');
    const plusButtons = document.querySelectorAll('[data-quantity-plus]');
    const minusButtons = document.querySelectorAll('[data-quantity-minus]');
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', function() {
        if (this.value < 1) {
          this.value = 1;
        }
      });
    });
    
    plusButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          input.value = parseInt(input.value) + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    
    minusButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input && parseInt(input.value) > 1) {
          input.value = parseInt(input.value) - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  showToast(message, type = 'success') {
    const existingToast = document.querySelector('.labratgifts-toast');
    if (existingToast) {
      existingToast.remove();
    }
    
    const toast = document.createElement('div');
    toast.className = `labratgifts-toast labratgifts-toast--${type}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.setAttribute('aria-atomic', 'true');
    
    const icon = type === 'success' 
      ? '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 10l2 2 6-6"/><circle cx="10" cy="10" r="9"/></svg>'
      : '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="10" cy="10" r="9"/><path d="M10 6v4"/><path d="M10 14h.01"/></svg>';
    
    toast.innerHTML = `
      <div class="labratgifts-toast__icon" aria-hidden="true">${icon}</div>
      <div class="labratgifts-toast__message">${message}</div>
      <button class="labratgifts-toast__close" aria-label="Close notification">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8"/>
        </svg>
      </button>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('labratgifts-toast--visible');
      
      const closeButton = toast.querySelector('.labratgifts-toast__close');
      closeButton.focus();
    }, 10);
    
    const closeToast = () => {
      toast.classList.remove('labratgifts-toast--visible');
      setTimeout(() => toast.remove(), 300);
    };
    
    const closeButton = toast.querySelector('.labratgifts-toast__close');
    closeButton.addEventListener('click', closeToast);
    closeButton.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeToast();
      }
    });
    
    setTimeout(closeToast, 8000);
  }

  initProductForms() {
    const productForms = document.querySelectorAll('.labratgifts-product-form');
    
    productForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');
        const originalText = submitButton ? submitButton.innerHTML : '';
        const isCollectionPage = document.querySelector('.labratgifts-collection');
        
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.innerHTML = '<span>Adding...</span>';
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            const addedItem = await response.json();
            
            if (submitButton) {
              submitButton.innerHTML = '<span>✓ Added to Cart!</span>';
            }
            
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            
            this.updateCartCount(cart.item_count);
            
            await this.refreshCartDrawer(cart);
            
            document.dispatchEvent(new CustomEvent('cart:item-added', {
              detail: {
                product_id: addedItem.product_id,
                variant_id: addedItem.variant_id,
                quantity: addedItem.quantity
              }
            }));
            
            if (isCollectionPage) {
              this.showToast('Product added to cart! Keep shopping for more deals.', 'success');
            } else {
              setTimeout(() => {
                this.openCartDrawer();
              }, 300);
            }
            
            setTimeout(() => {
              if (submitButton) {
                submitButton.disabled = false;
                submitButton.innerHTML = originalText;
              }
            }, 2000);
          } else {
            console.error('Failed to add item to cart');
            if (submitButton) {
              submitButton.innerHTML = '<span>Error - Try Again</span>';
              setTimeout(() => {
                submitButton.disabled = false;
                submitButton.innerHTML = originalText;
              }, 2000);
            }
            this.showToast('Failed to add product. Please try again.', 'error');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          if (submitButton) {
            submitButton.innerHTML = '<span>Error - Try Again</span>';
            setTimeout(() => {
              submitButton.disabled = false;
              submitButton.innerHTML = originalText;
            }, 2000);
          }
          this.showToast('An error occurred. Please try again.', 'error');
        }
      });
    });
  }

  initBuyNowButtons() {
    document.querySelectorAll('[data-buy-now-btn]').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        
        const variantId = button.dataset.variantId || button.closest('form')?.querySelector('[name="id"]')?.value;
        if (!variantId) {
          console.error('No variant ID found for Buy Now button');
          return;
        }
        
        const originalContent = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '<span>Processing...</span>';
        
        try {
          // Get the variant price from the page
          const priceWrapper = document.querySelector('[data-product-price-wrapper]');
          let variantPrice = parseInt(priceWrapper?.dataset.price) || 0;
          
          // If using campaign pricing, use the campaign price
          const useCampaignPricing = priceWrapper?.dataset.useCampaignPricing === 'true';
          if (!variantPrice) {
            // Fallback: try to get from variant select
            const variantSelect = document.querySelector('[id^="ProductVariant-"]');
            const selectedOption = variantSelect?.querySelector(`option[value="${variantId}"]`);
            variantPrice = parseInt(selectedOption?.dataset.variantPrice) || 0;
          }
          
          const quantity = parseInt(button.closest('form')?.querySelector('[name="quantity"]')?.value || document.querySelector('[data-quantity-input]')?.value || 1);
          const itemTotal = variantPrice * quantity;
          
          // Fetch current cart to check prospective total
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          const minimumThreshold = typeof window.getMinimumOrderThreshold === 'function' ? window.getMinimumOrderThreshold() : 0;
          const prospectiveTotal = cart.total_price + itemTotal;
          const wouldMeetMinimum = minimumThreshold === 0 || prospectiveTotal >= minimumThreshold;
          
          if (wouldMeetMinimum) {
            // Minimum would be met - proceed with adding and checkout
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: parseInt(variantId),
                quantity: quantity
              })
            });
            
            if (response.ok) {
              // Go directly to checkout
              window.location.href = '/checkout';
            } else {
              const error = await response.json();
              this.showToast(error.description || 'Failed to process. Please try again.', 'error');
              button.disabled = false;
              button.innerHTML = originalContent;
            }
          } else {
            // Minimum would NOT be met - show modal WITHOUT adding product first
            button.disabled = false;
            button.innerHTML = originalContent;
            
            // Set callback for modal to retry checkout after adding items
            window.buyNowPendingVariant = {
              variantId: parseInt(variantId),
              quantity: quantity
            };
            
            if (typeof window.showMinimumOrderModal === 'function') {
              window.showMinimumOrderModal();
            } else {
              this.showToast('Please add more items to reach the minimum order amount.', 'error');
            }
          }
        } catch (error) {
          console.error('Error with Buy Now:', error);
          this.showToast('An error occurred. Please try again.', 'error');
          button.disabled = false;
          button.innerHTML = originalContent;
        }
      });
    });
  }

  initProductDetailController() {
    const productSections = document.querySelectorAll('.labratgifts-product');
    
    productSections.forEach(section => {
      const priceWrapper = section.querySelector('[data-product-price-wrapper]');
      const variantSelect = section.querySelector('[id^="ProductVariant-"]');
      const quantityInput = section.querySelector('[data-quantity-input]');
      const variantInputs = section.querySelectorAll('[data-variant-option]');
      const skuElement = section.querySelector('[data-product-sku]');
      
      if (!priceWrapper || !variantSelect) return;
      
      let currentVariant = this.getSelectedVariant(variantSelect);
      
      // Update price display when quantity changes
      if (quantityInput) {
        const updateTotalPrice = () => {
          const quantity = parseInt(quantityInput.value) || 1;
          const totalWrapper = section.querySelector('[data-total-price-wrapper]');
          const totalPriceEl = section.querySelector('[data-total-price]');
          
          if (totalWrapper && totalPriceEl && currentVariant) {
            const useCampaignPricingAttr = priceWrapper?.dataset.useCampaignPricing;
            const useCampaignPricing = useCampaignPricingAttr === 'true';
            const campaignDiscount = useCampaignPricing ? (parseFloat(priceWrapper?.dataset.campaignDiscount) || 0) : 0;
            
            let unitPrice = currentVariant.price;
            
            if (useCampaignPricing && campaignDiscount > 0) {
              const discountMultiplier = (100 - campaignDiscount) / 100;
              unitPrice = Math.round(currentVariant.price * discountMultiplier);
            }
            
            const totalPrice = unitPrice * quantity;
            
            const formattedTotal = this.formatMoney(totalPrice);
            totalPriceEl.textContent = formattedTotal;
            
            if (quantity > 1) {
              totalWrapper.style.display = 'flex';
            } else {
              totalWrapper.style.display = 'none';
            }
          }
        };
        
        quantityInput.addEventListener('change', updateTotalPrice);
        quantityInput.addEventListener('input', updateTotalPrice);
        
        // Listen to quantity button events
        document.addEventListener('change', (e) => {
          if (e.target === quantityInput) {
            updateTotalPrice();
          }
        });
      }
      
      // Update all product info when variant changes
      if (variantInputs.length > 0) {
        variantInputs.forEach(input => {
          input.addEventListener('change', () => {
            currentVariant = this.getSelectedVariant(variantSelect);
            
            if (currentVariant) {
              this.updateProductInfo(section, currentVariant);
              
              // Update hidden variant input in the form
              const variantInput = section.querySelector('[data-variant-input], input[name="id"]');
              if (variantInput) {
                variantInput.value = currentVariant.id;
              }
              
              // Update Buy Now button's variant ID
              const buyNowBtn = section.querySelector('[data-buy-now-btn]');
              if (buyNowBtn) {
                buyNowBtn.dataset.variantId = currentVariant.id;
              }
              
              // Trigger quantity price update
              if (quantityInput) {
                quantityInput.dispatchEvent(new Event('change'));
              }
              
              // Dispatch custom event for other components (like gallery)
              document.dispatchEvent(new CustomEvent('product:variant-change', {
                detail: { variant: currentVariant }
              }));
            }
          });
        });
      }
    });
  }
  
  getSelectedVariant(variantSelect) {
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    if (!selectedOption) return null;
    
    return {
      id: selectedOption.value,
      price: parseFloat(selectedOption.dataset.variantPrice || selectedOption.text.match(/[\d,.]+/)?.[0]?.replace(',', '') || 0),
      compareAtPrice: parseFloat(selectedOption.dataset.variantComparePrice || 0),
      available: !selectedOption.disabled,
      sku: selectedOption.dataset.variantSku || '',
      mediaId: selectedOption.dataset.variantMediaId || null,
      campaignPrice: parseFloat(selectedOption.dataset.variantCampaignPrice || 0),
      campaignDiscount: parseFloat(selectedOption.dataset.variantCampaignDiscount || 0),
      useCampaign: selectedOption.dataset.variantUseCampaign === 'true'
    };
  }
  
  updateProductInfo(section, variant) {
    const priceWrapper = section.querySelector('[data-product-price-wrapper]');
    const priceCurrentEl = section.querySelector('[data-price-current]');
    const priceCompareEl = section.querySelector('[data-price-compare]');
    const savingsBadge = section.querySelector('[data-price-savings-badge]');
    const savingsText = section.querySelector('[data-price-savings-text]');
    const totalWrapper = section.querySelector('[data-total-price-wrapper]');
    
    const useCampaignPricing = variant.useCampaign === true;
    const campaignDiscount = useCampaignPricing ? (variant.campaignDiscount || 0) : 0;
    const campaignPrice = useCampaignPricing ? (variant.campaignPrice || variant.price) : variant.price;
    
    let displayPrice = variant.price;
    let comparePrice = variant.compareAtPrice;
    let savingsPercent = 0;
    let savings = 0;
    let isCampaignActive = false;
    
    if (useCampaignPricing && campaignDiscount > 0 && campaignPrice < variant.price) {
      displayPrice = campaignPrice;
      comparePrice = variant.price;
      savingsPercent = campaignDiscount;
      savings = variant.price - displayPrice;
      isCampaignActive = true;
      
      if (priceWrapper) {
        priceWrapper.dataset.price = displayPrice;
        priceWrapper.dataset.comparePrice = variant.price;
        priceWrapper.dataset.originalPrice = variant.price;
        priceWrapper.dataset.useCampaignPricing = 'true';
        priceWrapper.dataset.campaignDiscount = campaignDiscount;
      }
      
      if (totalWrapper) {
        totalWrapper.dataset.unitPrice = displayPrice;
      }
    } else {
      if (priceWrapper) {
        priceWrapper.dataset.price = variant.price;
        priceWrapper.dataset.comparePrice = variant.compareAtPrice || '';
        priceWrapper.dataset.originalPrice = variant.price;
        priceWrapper.dataset.useCampaignPricing = 'false';
        priceWrapper.dataset.campaignDiscount = '0';
      }
      
      if (totalWrapper) {
        totalWrapper.dataset.unitPrice = variant.price;
      }
    }
    
    if (priceCurrentEl) {
      const formattedPrice = this.formatMoney(displayPrice);
      priceCurrentEl.textContent = formattedPrice;
      
      if (isCampaignActive || (comparePrice && comparePrice > displayPrice)) {
        priceCurrentEl.classList.add('labratgifts-price--on-sale');
      } else {
        priceCurrentEl.classList.remove('labratgifts-price--on-sale');
      }
    }
    
    if (isCampaignActive) {
      if (priceCompareEl) {
        priceCompareEl.textContent = this.formatMoney(comparePrice);
        priceCompareEl.style.display = '';
      }
      
      if (savingsBadge) {
        savingsBadge.textContent = `SAVE ${savingsPercent}%`;
        savingsBadge.style.display = '';
      }
      
      if (savingsText) {
        savingsText.textContent = `You save ${this.formatMoney(savings)}`;
        savingsText.style.display = '';
      }
    } else if (comparePrice && comparePrice > variant.price) {
      if (priceCompareEl) {
        priceCompareEl.textContent = this.formatMoney(comparePrice);
        priceCompareEl.style.display = '';
      }
      
      savings = comparePrice - variant.price;
      savingsPercent = Math.round((savings / comparePrice) * 100);
      
      if (savingsBadge) {
        savingsBadge.textContent = `Save ${savingsPercent}%`;
        savingsBadge.style.display = '';
      }
      
      if (savingsText) {
        savingsText.textContent = `You save ${this.formatMoney(savings)}`;
        savingsText.style.display = '';
      }
    } else {
      if (priceCompareEl) priceCompareEl.style.display = 'none';
      if (savingsBadge) savingsBadge.style.display = 'none';
      if (savingsText) savingsText.style.display = 'none';
    }
    
    // Update SKU
    const skuEl = section.querySelector('[data-product-sku]');
    if (skuEl && variant.sku) {
      skuEl.textContent = variant.sku;
    }
    
    // Update availability
    const submitButton = section.querySelector('[type="submit"]');
    const stockInfo = section.querySelector('.labratgifts-product__stock-info');
    
    if (submitButton) {
      if (variant.available) {
        submitButton.disabled = false;
        submitButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 2h2l3 12h10l3-8H6"/>
            <circle cx="8" cy="18" r="1" fill="currentColor"/>
            <circle cx="16" cy="18" r="1" fill="currentColor"/>
          </svg>
          Add to Cart
        `;
        if (stockInfo) stockInfo.style.display = 'flex';
      } else {
        submitButton.disabled = true;
        submitButton.textContent = 'Sold Out';
        if (stockInfo) stockInfo.style.display = 'none';
      }
    }
  }
  
  formatMoney(cents) {
    // Basic money formatter - uses Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format);
    }
    
    // Fallback to basic formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  updateCartCount(count) {
    // CRITICAL: Update all cart count elements immediately
    const cartCounts = document.querySelectorAll('[data-cart-count]');
    cartCounts.forEach(el => {
      el.textContent = count;
    });
    
    // Toggle visibility of cart count badges
    const badges = document.querySelectorAll('.labratgifts-cart-count');
    badges.forEach(badge => {
      if (count > 0) {
        badge.classList.remove('labratgifts-cart-count--hidden');
      } else {
        badge.classList.add('labratgifts-cart-count--hidden');
      }
    });
    
    // Update cart icons visibility class
    const cartIcons = document.querySelectorAll('[data-cart-toggle]');
    cartIcons.forEach(icon => {
      if (count > 0) {
        icon.classList.add('has-items');
      } else {
        icon.classList.remove('has-items');
      }
    });
    
    // Dispatch event for other components to react
    document.dispatchEvent(new CustomEvent('cart:count-updated', { detail: { count } }));
  }

  openCartDrawer() {
    const cartDrawer = document.getElementById('cartDrawer');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  initSearchModal() {
    const searchToggleBtns = document.querySelectorAll('[data-search-toggle]');
    const searchModal = document.getElementById('searchModal');
    const searchInput = document.getElementById('searchModalInput');
    const searchCloseBtns = document.querySelectorAll('[data-search-close]');
    const recentSearchContainer = document.getElementById('recentSearches');
    const recentSearchTags = document.getElementById('recentSearchTags');
    const clearAllBtn = document.getElementById('clearAllSearches');

    if (!searchModal) return;

    const openSearchModal = () => {
      searchModal.classList.add('active');
      document.body.style.overflow = 'hidden';
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
      this.loadRecentSearches();
    };

    const closeSearchModal = () => {
      searchModal.classList.remove('active');
      document.body.style.overflow = '';
    };

    searchToggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openSearchModal();
      });
    });

    searchCloseBtns.forEach(btn => {
      btn.addEventListener('click', closeSearchModal);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('active')) {
        closeSearchModal();
      }
    });

    if (searchInput) {
      let searchTimeout;
      
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        
        clearTimeout(searchTimeout);
        
        if (query.length === 0) {
          this.hideSearchResults();
          return;
        }
        
        if (query.length < 2) return;
        
        searchTimeout = setTimeout(() => {
          this.performPredictiveSearch(query);
        }, 300);
      });
      
      searchInput.closest('form').addEventListener('submit', (e) => {
        const query = searchInput.value.trim();
        if (query) {
          this.saveRecentSearch(query);
        }
      });
    }

    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', () => {
        localStorage.removeItem('labratgifts_recent_searches');
        if (recentSearchContainer) {
          recentSearchContainer.style.display = 'none';
        }
      });
    }

    if (recentSearchTags) {
      recentSearchTags.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-remove-tag]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const tag = removeBtn.closest('.labratgifts-search-tag');
          if (tag) {
            const searchTerm = tag.dataset.search;
            this.removeRecentSearch(searchTerm);
          }
        }
      });
    }
  }

  loadRecentSearches() {
    const recentSearchContainer = document.getElementById('recentSearches');
    const recentSearchTags = document.getElementById('recentSearchTags');
    
    if (!recentSearchTags) return;

    const searches = JSON.parse(localStorage.getItem('labratgifts_recent_searches') || '[]');
    
    if (searches.length === 0) {
      if (recentSearchContainer) {
        recentSearchContainer.style.display = 'none';
      }
      return;
    }

    if (recentSearchContainer) {
      recentSearchContainer.style.display = 'block';
    }

    recentSearchTags.innerHTML = searches.map(search => {
      const escaped = search.replace(/[&<>"']/g, (char) => {
        const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return entities[char];
      });
      return `
        <a href="/search?q=${encodeURIComponent(search)}&type=product" class="labratgifts-search-tag" data-search="${escaped}">
          ${escaped}
          <button type="button" data-remove-tag aria-label="Remove ${escaped}">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="10" y1="4" x2="4" y2="10"></line>
              <line x1="4" y1="4" x2="10" y2="10"></line>
            </svg>
          </button>
        </a>
      `;
    }).join('');
  }

  saveRecentSearch(query) {
    let searches = JSON.parse(localStorage.getItem('labratgifts_recent_searches') || '[]');
    searches = searches.filter(s => s.toLowerCase() !== query.toLowerCase());
    searches.unshift(query);
    searches = searches.slice(0, 6);
    localStorage.setItem('labratgifts_recent_searches', JSON.stringify(searches));
  }

  removeRecentSearch(query) {
    let searches = JSON.parse(localStorage.getItem('labratgifts_recent_searches') || '[]');
    searches = searches.filter(s => s.toLowerCase() !== query.toLowerCase());
    localStorage.setItem('labratgifts_recent_searches', JSON.stringify(searches));
    this.loadRecentSearches();
  }

  resizeShopifyImage(url, size = '300x300') {
    if (!url) return '';
    
    const lastDotIndex = url.lastIndexOf('.');
    if (lastDotIndex === -1) return url;
    
    const beforeExtension = url.substring(0, lastDotIndex);
    const extension = url.substring(lastDotIndex);
    
    return `${beforeExtension}_${size}${extension}`;
  }

  async performPredictiveSearch(query) {
    const searchResults = document.getElementById('searchResults');
    const searchResultsContent = document.getElementById('searchResultsContent');
    const searchDefaultView = document.getElementById('searchDefaultView');
    
    if (!searchResults || !searchResultsContent) return;
    
    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    const sectionUrl = `${shopifyRoot}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection&resources[limit]=8&resources[options][unavailable_products]=last`;
    
    console.log('[Predictive Search] Fetching:', sectionUrl);
    
    try {
      const response = await fetch(sectionUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).catch(err => {
        console.warn('[Predictive Search] Section fetch failed, trying JSON API fallback:', err);
        return null;
      });
      
      if (response && response.ok) {
        console.log('[Predictive Search] Response status:', response.status);
        const html = await response.text();
        console.log('[Predictive Search] HTML received, length:', html.length);
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const searchContent = doc.querySelector('.labratgifts-predictive-search-results');
        
        if (searchContent && searchContent.innerHTML.trim()) {
          searchResultsContent.innerHTML = searchContent.innerHTML;
          searchDefaultView.style.display = 'none';
          searchResults.style.display = 'block';
          return;
        }
      }
      
      console.log('[Predictive Search] Using JSON API fallback');
      await this.performPredictiveSearchJSON(query);
      
    } catch (error) {
      console.error('[Predictive Search] Error:', error);
      try {
        await this.performPredictiveSearchJSON(query);
      } catch (fallbackError) {
        console.error('[Predictive Search] Fallback also failed:', fallbackError);
        this.showSearchError(query);
      }
    }
  }

  async performPredictiveSearchJSON(query) {
    const searchResults = document.getElementById('searchResults');
    const searchResultsContent = document.getElementById('searchResultsContent');
    const searchDefaultView = document.getElementById('searchDefaultView');
    
    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    const jsonUrl = `${shopifyRoot}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
    
    console.log('[Predictive Search JSON] Fetching:', jsonUrl);
    
    const response = await fetch(jsonUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('[Predictive Search JSON] Data received:', data);
    
    if (data.resources && data.resources.results && data.resources.results.products) {
      const products = data.resources.results.products;
      
      if (products.length === 0) {
        searchResultsContent.innerHTML = `
          <div class="labratgifts-search-no-results">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="35" cy="35" r="20"/>
              <path d="m50 50 20 20"/>
              <line x1="27" y1="35" x2="43" y2="35"/>
            </svg>
            <h3>No results found for "${this.escapeHtml(query)}"</h3>
            <p>Try different keywords or browse our collections</p>
          </div>
        `;
      } else {
        searchResultsContent.innerHTML = `
          <div class="labratgifts-search-results-header">
            <h3>Search Results for "${this.escapeHtml(query)}"</h3>
            <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}&type=product" class="labratgifts-view-all">View all results →</a>
          </div>
          <div class="labratgifts-search-results-grid">
            ${products.map(product => `
              <a href="${this.escapeHtml(product.url)}" class="labratgifts-search-result-item">
                <div class="labratgifts-search-result-image">
                  ${product.featured_image ? 
                    `<img src="${this.escapeHtml(this.resizeShopifyImage(product.featured_image, '300x300'))}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="150" height="150">` : 
                    `<div class="labratgifts-search-placeholder"><svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="8" y="8" width="44" height="44" rx="2"/><circle cx="22" cy="22" r="4"/><path d="M8 45 L25 28 L38 41"/><path d="M38 38 L45 31 L52 38 L52 52 L8 52"/></svg></div>`
                  }
                </div>
                <div class="labratgifts-search-result-info">
                  <h4>${this.escapeHtml(product.title)}</h4>
                  <p class="labratgifts-search-result-price">${this.formatMoney(product.price)}</p>
                  ${product.compare_at_price && product.compare_at_price > product.price ? 
                    `<p class="labratgifts-search-result-compare"><s>${this.formatMoney(product.compare_at_price)}</s></p>` : 
                    ''
                  }
                </div>
              </a>
            `).join('')}
          </div>
        `;
      }
      
      searchDefaultView.style.display = 'none';
      searchResults.style.display = 'block';
    }
  }

  showSearchError(query) {
    const searchResults = document.getElementById('searchResults');
    const searchResultsContent = document.getElementById('searchResultsContent');
    const searchDefaultView = document.getElementById('searchDefaultView');
    
    searchResultsContent.innerHTML = `
      <div class="labratgifts-search-error">
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="30" cy="30" r="25"/>
          <line x1="30" y1="20" x2="30" y2="32"/>
          <circle cx="30" cy="40" r="1.5" fill="currentColor"/>
        </svg>
        <h3>Search unavailable</h3>
        <p>Please try again in a moment or use the search page.</p>
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}&type=product" class="labratgifts-btn labratgifts-btn--primary" style="margin-top: 16px; display: inline-block;">
          Go to Search Page
        </a>
      </div>
    `;
    searchDefaultView.style.display = 'none';
    searchResults.style.display = 'block';
  }

  hideSearchResults() {
    const searchResults = document.getElementById('searchResults');
    const searchDefaultView = document.getElementById('searchDefaultView');
    
    if (searchResults) searchResults.style.display = 'none';
    if (searchDefaultView) searchDefaultView.style.display = 'block';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  initAccountModal() {
    const accountToggleBtn = document.querySelector('[data-account-toggle]');
    const accountModal = document.getElementById('accountModal');
    const accountCloseBtns = document.querySelectorAll('[data-account-close]');
    const loginTab = document.querySelector('[data-tab="login"]');
    const signupTab = document.querySelector('[data-tab="signup"]');
    const loginForm = document.getElementById('loginForm');
    const signupForm = document.getElementById('signupForm');

    if (!accountModal) return;

    const openAccountModal = () => {
      accountModal.classList.add('active');
      document.body.style.overflow = 'hidden';
    };

    const closeAccountModal = () => {
      accountModal.classList.remove('active');
      document.body.style.overflow = '';
      if (accountToggleBtn) accountToggleBtn.focus();
    };

    if (accountToggleBtn) {
      accountToggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        openAccountModal();
      });
    }

    accountCloseBtns.forEach(btn => {
      btn.addEventListener('click', closeAccountModal);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && accountModal.classList.contains('active')) {
        closeAccountModal();
      }
    });

    if (loginTab && signupTab && loginForm && signupForm) {
      loginTab.addEventListener('click', () => {
        loginTab.classList.add('labratgifts-account-tab--active');
        signupTab.classList.remove('labratgifts-account-tab--active');
        loginForm.style.display = 'block';
        signupForm.style.display = 'none';
      });

      signupTab.addEventListener('click', () => {
        signupTab.classList.add('labratgifts-account-tab--active');
        loginTab.classList.remove('labratgifts-account-tab--active');
        signupForm.style.display = 'block';
        loginForm.style.display = 'none';
      });
    }
  }

  initModernCartDrawer() {
    const cartToggleBtns = document.querySelectorAll('[data-cart-toggle]');
    const cartDrawer = document.getElementById('cartDrawer');
    const cartCloseBtns = document.querySelectorAll('[data-cart-close]');

    if (!cartDrawer) return;
    
    // Prevent duplicate initialization
    if (cartDrawer.dataset.initialized === 'true') return;
    cartDrawer.dataset.initialized = 'true';

    const openModernCartDrawer = async () => {
      cartDrawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Add loading state
      const drawerBody = cartDrawer.querySelector('.labratgifts-cart-drawer__body');
      if (drawerBody) {
        drawerBody.classList.add('labratgifts-cart-loading');
      }
      
      // Fetch latest cart data and refresh drawer
      try {
        const response = await fetch('/?section_id=cart-drawer');
        if (response.ok) {
          const html = await response.text();
          
          // Update cart drawer content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const newContent = tempDiv.querySelector('.labratgifts-cart-drawer__content');
          
          if (newContent) {
            const currentContent = cartDrawer.querySelector('.labratgifts-cart-drawer__content');
            if (currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
              
              // Update cart count in header from drawer count
              const cartCount = tempDiv.querySelector('.labratgifts-cart-drawer__count');
              if (cartCount) {
                const countMatch = cartCount.textContent.match(/\d+/);
                if (countMatch) {
                  this.updateCartCount(parseInt(countMatch[0]));
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing cart drawer:', error);
      } finally {
        // Remove loading state
        if (drawerBody) {
          drawerBody.classList.remove('labratgifts-cart-loading');
        }
      }
    };

    const closeModernCartDrawer = () => {
      cartDrawer.classList.remove('active');
      document.body.style.overflow = '';
    };
    
    // Store close function for external access
    this.closeCartDrawer = closeModernCartDrawer;
    this.openCartDrawer = openModernCartDrawer;

    cartToggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModernCartDrawer();
      });
    });

    cartCloseBtns.forEach(btn => {
      btn.addEventListener('click', closeModernCartDrawer);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('active')) {
        closeModernCartDrawer();
      }
    });

    // USE EVENT DELEGATION for cart drawer interactions
    // This ensures buttons work even after content is replaced via AJAX
    this.setupCartDrawerDelegation(cartDrawer);
  }

  setupCartDrawerDelegation(cartDrawer) {
    // Single event delegation handler for ALL cart drawer interactions
    cartDrawer.addEventListener('click', async (e) => {
      const target = e.target;
      
      // Handle close button clicks
      if (target.closest('[data-cart-close]')) {
        cartDrawer.classList.remove('active');
        document.body.style.overflow = '';
        return;
      }
      
      // Handle quantity increase
      const increaseBtn = target.closest('[data-qty-increase]');
      if (increaseBtn) {
        e.preventDefault();
        const line = increaseBtn.dataset.line;
        const input = cartDrawer.querySelector(`input[data-line="${line}"]`);
        if (input) {
          const newQty = parseInt(input.value) + 1;
          await this.updateCartItem(line, newQty);
        }
        return;
      }
      
      // Handle quantity decrease
      const decreaseBtn = target.closest('[data-qty-decrease]');
      if (decreaseBtn) {
        e.preventDefault();
        const line = decreaseBtn.dataset.line;
        const input = cartDrawer.querySelector(`input[data-line="${line}"]`);
        if (input) {
          const newQty = Math.max(0, parseInt(input.value) - 1);
          await this.updateCartItem(line, newQty);
        }
        return;
      }
      
      // Handle remove item
      const removeBtn = target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        const line = removeBtn.dataset.line;
        await this.updateCartItem(line, 0);
        return;
      }
    });
    
    // Handle input change for direct quantity entry
    cartDrawer.addEventListener('change', async (e) => {
      const input = e.target.closest('.labratgifts-qty-input');
      if (input) {
        const line = input.dataset.line;
        const newQty = Math.max(0, parseInt(input.value) || 0);
        await this.updateCartItem(line, newQty);
      }
    });
  }

  async updateCartItem(line, quantity) {
    const cartDrawer = document.getElementById('cartDrawer');
    
    // Show loading state on the item being updated
    if (cartDrawer) {
      const item = cartDrawer.querySelector(`[data-line-item="${line}"]`);
      if (item) item.classList.add('labratgifts-cart-item--updating');
    }
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (response.ok) {
        const cart = await response.json();
        await this.refreshCartDrawer(cart);
        
        // Show feedback
        if (quantity === 0) {
          this.showToast('Item removed from cart', 'success');
        }
      } else {
        this.showToast('Failed to update cart. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showToast('An error occurred. Please try again.', 'error');
    }
  }

  async refreshCartDrawer(cart) {
    // Update cart count in header immediately
    this.updateCartCount(cart.item_count);
    
    const cartDrawer = document.getElementById('cartDrawer');
    if (!cartDrawer) return;
    
    try {
      // Fetch updated cart drawer HTML from cart drawer section
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      
      // Update cart drawer content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newContent = tempDiv.querySelector('.labratgifts-cart-drawer__content');
      
      if (newContent) {
        const currentContent = cartDrawer.querySelector('.labratgifts-cart-drawer__content');
        if (currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }
      }
      
      // Dispatch event for other components to react
      document.dispatchEvent(new CustomEvent('cart:refreshed', { detail: { cart } }));
      
      // NOTE: No need to re-initialize - we use event delegation now!
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  initQuickView() {
    if (typeof QuickView !== 'undefined') {
      this.quickViewInstance = new QuickView();
    }
  }

  initProductGallery() {
    if (typeof ProductGallery !== 'undefined') {
      const galleries = document.querySelectorAll('.labratgifts-product-gallery');
      galleries.forEach(gallery => new ProductGallery(gallery));
    }
  }

  initFBT() {
    if (typeof CartFBT !== 'undefined') {
      this.fbtInstance = new CartFBT();
    }
  }

  initCompare() {
    const drawer = document.getElementById('compare-drawer');
    if (!drawer) return;

    try {
      this.compareProducts = JSON.parse(localStorage.getItem('labratgifts_compare') || '[]');
    } catch (error) {
      console.warn('localStorage not available, compare feature disabled:', error);
      this.compareProducts = [];
    }

    const closeButtons = drawer.querySelectorAll('[data-compare-close]');
    const clearButton = drawer.querySelector('[data-compare-clear]');
    const compareList = document.getElementById('compare-products-list');

    document.addEventListener('click', (e) => {
      const compareBtn = e.target.closest('[data-compare-toggle]');
      if (compareBtn) {
        e.preventDefault();
        const productHandle = compareBtn.dataset.compareToggle;
        const productCard = compareBtn.closest('[data-product-id]');
        
        if (productCard) {
          const productData = {
            id: productCard.dataset.productId,
            handle: productCard.dataset.productHandle,
            title: productCard.dataset.productTitle,
            price: productCard.dataset.productPrice,
            image: productCard.dataset.productImage,
            url: productCard.dataset.productUrl
          };
          this.toggleCompareProduct(productData, compareBtn);
        }
      }
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeCompare(drawer));
    });

    if (clearButton) {
      clearButton.addEventListener('click', () => this.clearCompare());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('active')) {
        this.closeCompare(drawer);
      }
    });

    this.updateCompareUI();
  }

  toggleCompareProduct(product, button) {
    const index = this.compareProducts.findIndex(p => p.id === product.id);
    
    if (index > -1) {
      this.compareProducts.splice(index, 1);
      button.classList.remove('active');
    } else {
      if (this.compareProducts.length >= 4) {
        alert('You can compare up to 4 products');
        return;
      }
      this.compareProducts.push(product);
      button.classList.add('active');
    }

    try {
      localStorage.setItem('labratgifts_compare', JSON.stringify(this.compareProducts));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
    
    this.updateCompareUI();
    this.openCompare();
  }

  updateCompareUI() {
    const compareList = document.getElementById('compare-products-list');
    const viewButton = document.getElementById('compare-view-button');
    
    if (!compareList) return;

    if (this.compareProducts.length === 0) {
      compareList.innerHTML = `
        <div class="labratgifts-compare-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 8L8 20V44L32 56L56 44V20L32 8Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
            <path d="M32 32V56" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M8 20L32 32L56 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <p>No products to compare</p>
          <p class="labratgifts-compare-empty-hint">Click the compare icon on products to add them here</p>
        </div>
      `;
      if (viewButton) viewButton.style.display = 'none';
    } else {
      compareList.innerHTML = this.compareProducts.map(product => `
        <div class="labratgifts-compare-item" data-compare-id="${product.id}">
          <div class="labratgifts-compare-item-image">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div class="labratgifts-compare-item-details">
            <h3 class="labratgifts-compare-item-title">${product.title}</h3>
            <div class="labratgifts-compare-item-price">$${(parseFloat(product.price) / 100).toFixed(2)}</div>
          </div>
          <button type="button" class="labratgifts-compare-item-remove" data-remove-compare="${product.id}" aria-label="Remove ${product.title}">×</button>
        </div>
      `).join('');

      if (viewButton) viewButton.style.display = this.compareProducts.length >= 2 ? 'block' : 'none';

      compareList.querySelectorAll('[data-remove-compare]').forEach(btn => {
        btn.addEventListener('click', () => {
          const productId = btn.dataset.removeCompare;
          this.compareProducts = this.compareProducts.filter(p => p.id !== productId);
          try {
            localStorage.setItem('labratgifts_compare', JSON.stringify(this.compareProducts));
          } catch (error) {
            console.warn('Failed to save to localStorage:', error);
          }
          this.updateCompareUI();
          this.updateCompareButtons();
        });
      });
    }

    this.updateCompareButtons();
  }

  updateCompareButtons() {
    document.querySelectorAll('[data-compare-toggle]').forEach(btn => {
      const productCard = btn.closest('[data-product-id]');
      if (productCard) {
        const productId = productCard.dataset.productId;
        const isInCompare = this.compareProducts.some(p => p.id === productId);
        btn.classList.toggle('active', isInCompare);
      }
    });
  }

  openCompare() {
    const drawer = document.getElementById('compare-drawer');
    if (drawer) {
      this.compareTrigger = document.activeElement;
      drawer.classList.add('active');
      drawer.setAttribute('aria-hidden', 'false');
      this.trapFocus(drawer);
    }
  }

  closeCompare(drawer) {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    
    if (this.compareTrigger) {
      this.compareTrigger.focus();
      this.compareTrigger = null;
    }
  }

  clearCompare() {
    this.compareProducts = [];
    try {
      localStorage.setItem('labratgifts_compare', JSON.stringify(this.compareProducts));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
    this.updateCompareUI();
  }

  // =====================================
  // Wishlist Functions
  // =====================================

  initWishlist() {
    const drawer = document.getElementById('wishlist-drawer');
    const wishlistGrid = document.querySelector('[data-wishlist-grid]');
    
    // Only initialize if wishlist is enabled
    if (!drawer && !wishlistGrid) return;

    // Initialize wishlist products array
    try {
      this.wishlistProducts = JSON.parse(localStorage.getItem('labratgifts_wishlist') || '[]');
    } catch (error) {
      console.warn('localStorage not available, wishlist feature disabled:', error);
      this.wishlistProducts = [];
    }

    // Set up event listeners for wishlist buttons
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('[data-wishlist-toggle]');
      if (wishlistBtn) {
        e.preventDefault();
        const productId = wishlistBtn.dataset.wishlistToggle;
        const productCard = wishlistBtn.closest('[data-product-id]');
        
        if (productCard) {
          const productData = {
            id: productCard.dataset.productId,
            handle: productCard.dataset.productHandle,
            title: productCard.dataset.productTitle,
            price: productCard.dataset.productPrice,
            image: productCard.dataset.productImage,
            url: productCard.dataset.productUrl
          };
          this.toggleWishlistProduct(productData, wishlistBtn);
        }
      }
    });

    // Set up close buttons
    if (drawer) {
      const closeButtons = drawer.querySelectorAll('[data-wishlist-close]');
      closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.closeWishlist(drawer));
      });

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('active')) {
          this.closeWishlist(drawer);
        }
      });
    }

    // Set up clear button
    const clearButtons = document.querySelectorAll('[data-wishlist-clear]');
    clearButtons.forEach(btn => {
      btn.addEventListener('click', () => this.clearWishlist());
    });

    // Update UI on load
    this.updateWishlistUI();
  }

  toggleWishlistProduct(product, button) {
    // Check if user is logged in (for Shopify customer accounts)
    const isLoggedIn = typeof window.Shopify !== 'undefined' && 
                       window.Shopify.customer;

    const requireLogin = document.querySelector('[data-wishlist-require-login]')?.dataset.wishlistRequireLogin === 'true';

    // If login is required and user is not logged in, show login modal
    if (requireLogin && !isLoggedIn) {
      this.showLoginPrompt();
      return;
    }

    const index = this.wishlistProducts.findIndex(p => p.id === product.id);
    
    if (index > -1) {
      // Remove from wishlist
      this.wishlistProducts.splice(index, 1);
      button.classList.remove('active');
      button.setAttribute('aria-label', 'Add to wishlist');
      this.showNotification('Removed from wishlist', 'success');
    } else {
      // Add to wishlist
      this.wishlistProducts.push(product);
      button.classList.add('active');
      button.setAttribute('aria-label', 'Remove from wishlist');
      this.showNotification('Added to wishlist', 'success');
    }

    // Save to localStorage
    try {
      localStorage.setItem('labratgifts_wishlist', JSON.stringify(this.wishlistProducts));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
    
    // Update UI
    this.updateWishlistUI();
    
    // Open drawer if configured
    const displayType = document.querySelector('[data-wishlist-drawer]')?.dataset.wishlistDisplay || 'drawer';
    if (displayType === 'drawer') {
      this.openWishlist();
    }
  }

  updateWishlistUI() {
    // Update drawer UI
    const wishlistList = document.getElementById('wishlist-products-list');
    if (wishlistList) {
      this.updateWishlistDrawer(wishlistList);
    }

    // Update account page grid
    const wishlistGrid = document.getElementById('wishlist-grid');
    if (wishlistGrid) {
      this.updateWishlistGrid(wishlistGrid);
    }

    // Update wishlist button states
    this.updateWishlistButtons();
  }

  updateWishlistDrawer(container) {
    const emptyState = container.querySelector('#wishlist-drawer-empty');
    const viewButton = document.getElementById('view-wishlist-btn');
    const clearButton = container.closest('[data-wishlist-drawer]')?.querySelector('[data-wishlist-clear]');

    if (this.wishlistProducts.length === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (viewButton) viewButton.style.display = 'none';
      if (clearButton) clearButton.style.display = 'none';
      
      // Remove existing product items
      container.querySelectorAll('.labratgifts-wishlist-item').forEach(item => item.remove());
    } else {
      if (emptyState) emptyState.style.display = 'none';
      if (viewButton) viewButton.style.display = 'block';
      if (clearButton) clearButton.style.display = 'block';

      // Update product list
      container.innerHTML = this.wishlistProducts.map(product => `
        <div class="labratgifts-wishlist-item" data-wishlist-id="${product.id}">
          <div class="labratgifts-wishlist-item-image">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div class="labratgifts-wishlist-item-details">
            <h3 class="labratgifts-wishlist-item-title">${product.title}</h3>
            <div class="labratgifts-wishlist-item-price">$${(parseFloat(product.price) / 100).toFixed(2)}</div>
            <a href="${product.url}" class="labratgifts-btn labratgifts-btn--small">View Product</a>
          </div>
          <button type="button" class="labratgifts-wishlist-item-remove" data-remove-wishlist="${product.id}" aria-label="Remove ${product.title}">×</button>
        </div>
      `).join('');

      // Set up remove buttons
      container.querySelectorAll('[data-remove-wishlist]').forEach(btn => {
        btn.addEventListener('click', () => {
          const productId = btn.dataset.removeWishlist;
          this.wishlistProducts = this.wishlistProducts.filter(p => p.id !== productId);
          try {
            localStorage.setItem('labratgifts_wishlist', JSON.stringify(this.wishlistProducts));
          } catch (error) {
            console.warn('Failed to save to localStorage:', error);
          }
          this.updateWishlistUI();
        });
      });
    }
  }

  updateWishlistGrid(container) {
    const emptyState = document.getElementById('wishlist-empty-state');
    const countText = document.getElementById('wishlist-count-text');
    const clearButton = document.querySelector('[data-wishlist-clear]');

    if (countText) {
      const count = this.wishlistProducts.length;
      countText.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    }

    if (this.wishlistProducts.length === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (clearButton) clearButton.style.display = 'none';
      
      // Remove existing product cards
      container.querySelectorAll('.labratgifts-product-card').forEach(card => card.remove());
    } else {
      if (emptyState) emptyState.style.display = 'none';
      if (clearButton) clearButton.style.display = 'block';

      // Create product cards for wishlist items
      const productsHTML = this.wishlistProducts.map(product => `
        <div class="labratgifts-product-card" data-product-id="${product.id}">
          <div class="labratgifts-product-card__image-container">
            <a href="${product.url}">
              ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
            </a>
            <button type="button" class="labratgifts-wishlist-remove-btn" data-remove-wishlist="${product.id}" aria-label="Remove from wishlist">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="15" y1="5" x2="5" y2="15"></line>
                <line x1="5" y1="5" x2="15" y2="15"></line>
              </svg>
            </button>
          </div>
          <div class="labratgifts-product-card__info">
            <h3 class="labratgifts-product-card__title">
              <a href="${product.url}">${product.title}</a>
            </h3>
            <div class="labratgifts-product-card__price">
              <span class="labratgifts-price">$${(parseFloat(product.price) / 100).toFixed(2)}</span>
            </div>
            <a href="${product.url}" class="labratgifts-btn labratgifts-btn--primary labratgifts-btn--full">View Product</a>
          </div>
        </div>
      `).join('');

      container.innerHTML = productsHTML;

      // Set up remove buttons
      container.querySelectorAll('[data-remove-wishlist]').forEach(btn => {
        btn.addEventListener('click', () => {
          const productId = btn.dataset.removeWishlist;
          this.wishlistProducts = this.wishlistProducts.filter(p => p.id !== productId);
          try {
            localStorage.setItem('labratgifts_wishlist', JSON.stringify(this.wishlistProducts));
          } catch (error) {
            console.warn('Failed to save to localStorage:', error);
          }
          this.updateWishlistUI();
        });
      });
    }
  }

  updateWishlistButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const productCard = btn.closest('[data-product-id]');
      if (productCard) {
        const productId = productCard.dataset.productId;
        const isInWishlist = this.wishlistProducts.some(p => p.id === productId);
        
        // Toggle active class and icon visibility
        btn.classList.toggle('active', isInWishlist);
        const outlineIcon = btn.querySelector('.labratgifts-wishlist-icon');
        const filledIcon = btn.querySelector('.labratgifts-wishlist-icon-filled');
        
        if (outlineIcon && filledIcon) {
          if (isInWishlist) {
            outlineIcon.style.display = 'none';
            filledIcon.style.display = 'block';
            btn.setAttribute('aria-label', 'Remove from wishlist');
          } else {
            outlineIcon.style.display = 'block';
            filledIcon.style.display = 'none';
            btn.setAttribute('aria-label', 'Add to wishlist');
          }
        }
      }
    });
  }

  openWishlist() {
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer) {
      this.wishlistTrigger = document.activeElement;
      drawer.classList.add('active');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.trapFocus(drawer);
    }
  }

  closeWishlist(drawer) {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    if (this.wishlistTrigger) {
      this.wishlistTrigger.focus();
      this.wishlistTrigger = null;
    }
  }

  clearWishlist() {
    if (confirm('Are you sure you want to clear your entire wishlist?')) {
      this.wishlistProducts = [];
      try {
        localStorage.setItem('labratgifts_wishlist', JSON.stringify(this.wishlistProducts));
      } catch (error) {
        console.warn('Failed to save to localStorage:', error);
      }
      this.updateWishlistUI();
      this.showNotification('Wishlist cleared', 'success');
    }
  }

  showLoginPrompt() {
    // Try to open the account modal if it exists
    const accountModal = document.getElementById('accountModal');
    if (accountModal && typeof this.initAccountModal === 'function') {
      accountModal.classList.add('active');
      document.body.style.overflow = 'hidden';
    } else {
      alert('Please sign in to save items to your wishlist');
      window.location.href = '/account/login';
    }
  }

  showNotification(message, type = 'info') {
    // Simple notification - can be enhanced
    const notification = document.createElement('div');
    notification.className = `labratgifts-notification labratgifts-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4caf50' : '#333'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  initProductGallery() {
    if (typeof ProductGallery !== 'undefined') {
      const galleries = document.querySelectorAll('.labratgifts-product-gallery');
      galleries.forEach(gallery => {
        const galleryInstance = new ProductGallery(gallery);
        console.log('Initializing Product Gallery', { 
          thumbnailCount: galleryInstance.thumbnails.length,
          slidesCount: galleryInstance.slides.length
        });
      });
    }
  }

  openImageZoom() {
    const mainImage = document.getElementById('main-product-image');
    const zoomModal = document.getElementById('image-zoom-modal');
    const zoomImage = zoomModal.querySelector('img');

    if (mainImage && zoomModal && zoomImage) {
      const zoomSrc = mainImage.dataset.zoom || mainImage.src;
      zoomImage.src = zoomSrc;
      zoomImage.alt = mainImage.alt;
      zoomModal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  closeImageZoom() {
    const zoomModal = document.getElementById('image-zoom-modal');
    if (zoomModal) {
      zoomModal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (firstFocusable) {
      firstFocusable.focus();
    }

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    
    const observer = new MutationObserver(() => {
      if (!element.classList.contains('active')) {
        element.removeEventListener('keydown', handleTabKey);
        observer.disconnect();
      }
    });
    
    observer.observe(element, { attributes: true, attributeFilter: ['class'] });
  }
}

// Initialize theme
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.labRatTheme = new LabRatGiftsTheme();
    
    // Expose critical cart functions globally for popup components
    window.showToast = (message, type) => window.labRatTheme.showToast(message, type);
    window.refreshCartDrawer = (cart) => window.labRatTheme.refreshCartDrawer(cart);
    window.updateCartCount = (count) => window.labRatTheme.updateCartCount(count);
  });
} else {
  window.labRatTheme = new LabRatGiftsTheme();
  
  // Expose critical cart functions globally for popup components
  window.showToast = (message, type) => window.labRatTheme.showToast(message, type);
  window.refreshCartDrawer = (cart) => window.labRatTheme.refreshCartDrawer(cart);
  window.updateCartCount = (count) => window.labRatTheme.updateCartCount(count);
}

/**
 * Read More Component
 * Collapses long descriptions and shows a "Read More" button
 * Counts paragraphs (or heading elements) to determine truncation point
 */
function initReadMore() {
  const readMoreContainers = document.querySelectorAll('[data-read-more]');
  
  readMoreContainers.forEach(container => {
    const content = container.querySelector('[data-read-more-content]');
    const button = container.querySelector('[data-read-more-button]');
    const buttonText = button?.querySelector('.labratgifts-read-more__button-text');
    
    if (!content || !button) return;
    
    const paragraphCount = parseInt(container.dataset.paragraphCount) || 3;
    const readMoreText = container.dataset.readMoreText || 'Read More';
    const readLessText = container.dataset.readLessText || 'Read Less';
    
    // Find all block-level elements that should count as "paragraphs"
    const blockElements = content.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, table');
    
    // If content is short enough, no need for truncation
    if (blockElements.length <= paragraphCount) {
      container.classList.add('labratgifts-read-more--no-truncation');
      return;
    }
    
    // Calculate height at the truncation point
    let truncationHeight = 0;
    let elementsToShow = 0;
    
    for (let i = 0; i < blockElements.length && elementsToShow < paragraphCount; i++) {
      const el = blockElements[i];
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const marginTop = parseFloat(style.marginTop);
      const marginBottom = parseFloat(style.marginBottom);
      
      // Get element's position relative to content container
      const contentRect = content.getBoundingClientRect();
      const elementBottom = rect.bottom - contentRect.top + marginBottom;
      
      truncationHeight = elementBottom;
      elementsToShow++;
    }
    
    // Add some padding
    truncationHeight = Math.max(truncationHeight, 150);
    
    // Set CSS variable for max-height
    container.style.setProperty('--read-more-height', `${truncationHeight}px`);
    
    // Start collapsed
    container.classList.add('labratgifts-read-more--collapsed');
    
    // Toggle function
    function toggleReadMore() {
      const isExpanded = container.classList.contains('labratgifts-read-more--expanded');
      
      if (isExpanded) {
        // Collapse
        container.classList.remove('labratgifts-read-more--expanded');
        container.classList.add('labratgifts-read-more--collapsed');
        button.setAttribute('aria-expanded', 'false');
        if (buttonText) buttonText.textContent = readMoreText;
        
        // Scroll back to top of content on mobile
        if (window.innerWidth <= 768) {
          const tabPanel = container.closest('.labratgifts-tabs__panel');
          if (tabPanel) {
            tabPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } else {
        // Expand
        container.classList.remove('labratgifts-read-more--collapsed');
        container.classList.add('labratgifts-read-more--expanded');
        button.setAttribute('aria-expanded', 'true');
        if (buttonText) buttonText.textContent = readLessText;
      }
    }
    
    button.addEventListener('click', toggleReadMore);
  });
}

// Initialize Read More on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReadMore);
} else {
  initReadMore();
}

/**
 * Product Card - Quick Add with Variant Selection
 * Allows users to select variants and add to cart directly from collection pages
 */
document.addEventListener('DOMContentLoaded', function() {
  const productCards = document.querySelectorAll('[data-product-form]');
  
  productCards.forEach(card => {
    const variantSwatches = card.closest('.labratgifts-product-card').querySelectorAll('[data-variant-value]');
    const variantDataScript = card.closest('.labratgifts-product-card').querySelector('[data-product-variants]');
    const addToCartBtn = card.querySelector('[data-add-to-cart-btn]');
    const selectOptionsLink = card.querySelector('[data-select-options-link]');
    const variantIdInput = card.querySelector('[data-variant-id]');
    
    if (!variantSwatches.length || !variantDataScript) return;
    
    // Parse variant data
    let variantData;
    try {
      variantData = JSON.parse(variantDataScript.textContent);
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      return;
    }
    
    const variants = variantData.variants;
    
    // Handle variant swatch clicks
    variantSwatches.forEach(swatch => {
      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        
        // Update active state
        variantSwatches.forEach(s => s.classList.remove('active'));
        this.classList.add('active');
        
        // Get selected option value
        const selectedOption = this.dataset.variantValue;
        
        // Find matching variant
        const matchedVariant = variants.find(v => 
          v.option1 === selectedOption || 
          v.option2 === selectedOption || 
          v.option3 === selectedOption
        );
        
        if (matchedVariant) {
          // Update variant ID
          variantIdInput.value = matchedVariant.id;
          
          // Show Add to Cart button, hide Select Options
          if (matchedVariant.available) {
            addToCartBtn.style.display = 'flex';
            if (selectOptionsLink) selectOptionsLink.style.display = 'none';
            
            // Update button text if needed
            const btnText = addToCartBtn.querySelector('[data-btn-text]');
            if (btnText) {
              btnText.textContent = 'Add to Cart';
            }
            
            // Enable button
            addToCartBtn.disabled = false;
          } else {
            // Variant sold out
            addToCartBtn.style.display = 'flex';
            if (selectOptionsLink) selectOptionsLink.style.display = 'none';
            
            const btnText = addToCartBtn.querySelector('[data-btn-text]');
            if (btnText) {
              btnText.textContent = 'Sold Out';
            }
            
            addToCartBtn.disabled = true;
          }
        }
      });
    });
    
    // Handle Add to Cart submission
    card.addEventListener('submit', function(e) {
      e.preventDefault();
      
      const formData = new FormData(this);
      const submitBtn = this.querySelector('[data-add-to-cart-btn]');
      const btnText = submitBtn.querySelector('[data-btn-text]') || submitBtn.querySelector('span');
      const originalText = btnText.textContent;
      
      // Loading state
      submitBtn.disabled = true;
      btnText.textContent = 'Adding...';
      
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        // Success state
        btnText.textContent = 'Added!';
        setTimeout(() => {
          btnText.textContent = originalText;
          submitBtn.disabled = false;
        }, 1500);
        
        // Dispatch cart update event for cart drawer
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        
        // Optional: Open cart drawer if it exists
        const cartDrawerTrigger = document.querySelector('[data-cart-drawer-trigger]');
        if (cartDrawerTrigger) {
          cartDrawerTrigger.click();
        }
      })
      .catch(error => {
        console.error('Add to cart error:', error);
        btnText.textContent = 'Error';
        setTimeout(() => {
          btnText.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      });
    });
  });
});

// Product Description Tabs Class
class ProductDescriptionTabs {
  constructor(container) {
    this.container = container;
    this.tabs = container.querySelectorAll('[data-tab]');
    this.panels = container.querySelectorAll('[data-panel]');
    
    if (this.tabs.length === 0 || this.panels.length === 0) return;
    
    this.init();
  }
  
  init() {
    // Add click event listeners to tabs
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchTab(index);
      });
      
      // Keyboard navigation
      tab.addEventListener('keydown', (e) => {
        this.handleKeyboard(e, index);
      });
    });
    
    // Check for hash in URL for deep linking
    this.checkHashOnLoad();
  }
  
  switchTab(index) {
    // Deactivate all tabs and panels
    this.tabs.forEach(tab => {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });
    
    this.panels.forEach(panel => {
      panel.classList.remove('active');
    });
    
    // Activate selected tab and panel
    this.tabs[index].classList.add('active');
    this.tabs[index].setAttribute('aria-selected', 'true');
    this.tabs[index].setAttribute('tabindex', '0');
    
    this.panels[index].classList.add('active');
    
    // Update URL hash for deep linking
    const tabName = this.tabs[index].dataset.tab;
    if (tabName) {
      history.replaceState(null, null, `#${tabName}`);
    }
  }
  
  handleKeyboard(e, currentIndex) {
    let newIndex = currentIndex;
    
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }
    
    this.switchTab(newIndex);
    this.tabs[newIndex].focus();
  }
  
  checkHashOnLoad() {
    const hash = window.location.hash.substring(1);
    if (!hash) return;
    
    // Find tab matching hash
    this.tabs.forEach((tab, index) => {
      if (tab.dataset.tab === hash) {
        this.switchTab(index);
      }
    });
  }
}

// Product Description Accordion Class
class ProductDescriptionAccordion {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('[data-accordion-item]');
    
    if (this.items.length === 0) return;
    
    this.init();
  }
  
  init() {
    this.items.forEach(item => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');
      
      if (!trigger || !content) return;
      
      // Check if this panel should default to open
      const defaultOpen = content.dataset.defaultOpen === 'true';
      
      // Set initial ARIA attributes based on default state
      const isExpanded = defaultOpen;
      trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      content.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
      
      // Set initial max-height for animation
      if (isExpanded) {
        content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        content.style.maxHeight = '0';
      }
      
      // Click event
      trigger.addEventListener('click', () => {
        this.toggle(trigger, content);
      });
      
      // Keyboard event
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle(trigger, content);
        }
      });
    });
  }
  
  toggle(trigger, content) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    
    // Toggle ARIA attributes
    trigger.setAttribute('aria-expanded', !isExpanded);
    content.setAttribute('aria-hidden', isExpanded);
    
    // Animate height
    if (!isExpanded) {
      // Opening
      content.style.maxHeight = content.scrollHeight + 'px';
    } else {
      // Closing
      content.style.maxHeight = '0';
    }
  }
}

// ============================================
// INITIALIZE PRODUCT DESCRIPTION COMPONENTS
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Product Description Tabs
  const tabsContainers = document.querySelectorAll('[data-product-tabs]');
  tabsContainers.forEach(container => {
    new ProductDescriptionTabs(container);
  });
  
  // Initialize Product Description Accordions
  const accordionContainers = document.querySelectorAll('[data-accordion]');
  accordionContainers.forEach(container => {
    new ProductDescriptionAccordion(container);
  });
});

// ============================================
// STICKY HEADER - Enhanced Scroll Behavior
// ============================================
const stickyHeader = document.querySelector('#shopify-section-header-with-mega-menu .labratgifts-header[data-sticky="true"]');

if (stickyHeader) {
  let lastScroll = 0;
  
  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;
    
    // Add shadow when scrolled down
    if (currentScroll > 50) {
      stickyHeader.classList.add('scrolled');
    } else {
      stickyHeader.classList.remove('scrolled');
    }
    
    lastScroll = currentScroll;
  });
}

// ============================================
// GLOBAL SIDEBAR - Mobile Drawer & Sticky Functionality
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  const sidebar = document.querySelector('.labratgifts-global-sidebar');
  const sidebarToggle = document.querySelector('.labratgifts-sidebar-toggle-header');
  const sidebarClose = document.querySelector('.labratgifts-sidebar-close');
  const sidebarOverlay = document.querySelector('.labratgifts-sidebar-overlay');
  const body = document.body;
  
  // Apply sticky settings from theme editor
  if (sidebar) {
    const stickyBehavior = sidebar.getAttribute('data-sidebar-behavior');
    const stickyTop = parseInt(sidebar.getAttribute('data-sidebar-top')) || 10;
    const maxHeight = parseInt(sidebar.getAttribute('data-sidebar-max-height')) || 600;
    
    // Get the section wrapper (parent element with class labratgifts-section-sidebar)
    const sidebarWrapper = sidebar.closest('.labratgifts-section-sidebar');
    
    if (stickyBehavior === 'sticky' && sidebarWrapper) {
      // Apply CSS variables to the wrapper for proper sticky positioning
      sidebarWrapper.style.setProperty('--sidebar-sticky-top', `${stickyTop}px`);
      sidebarWrapper.style.setProperty('--sidebar-max-height', `${maxHeight}px`);
      
      // Find the main content area and footer to detect when main content ends
      const mainContent = document.querySelector('.labratgifts-main, main, [role="main"], .labratgifts-layout-wrapper--with-sidebar');
      const footer = document.querySelector('footer, .labratgifts-footer, #shopify-section-footer');
      
      if (mainContent || footer) {
        const handleSidebarScroll = () => {
          let shouldBeSticky = true;
          
          if (mainContent) {
            const mainRect = mainContent.getBoundingClientRect();
            const sidebarRect = sidebarWrapper.getBoundingClientRect();
            const sidebarHeight = sidebarWrapper.offsetHeight;
            
            // Check if the main content bottom is above the viewport bottom
            // This means the main content has ended
            const mainContentBottom = mainRect.bottom;
            const sidebarBottom = sidebarRect.top + sidebarHeight;
            
            // When main content ends (bottom is visible), stop sticky
            if (mainContentBottom < sidebarBottom + 50) {
              shouldBeSticky = false;
            }
          }
          
          // Also check footer - if footer is approaching, stop sticky
          if (footer && shouldBeSticky) {
            const footerRect = footer.getBoundingClientRect();
            const sidebarRect = sidebarWrapper.getBoundingClientRect();
            const sidebarHeight = sidebarWrapper.offsetHeight;
            
            // If footer top is close to sidebar bottom, stop sticky
            if (footerRect.top < sidebarRect.top + sidebarHeight + 30) {
              shouldBeSticky = false;
            }
          }
          
          // Apply or remove sticky behavior
          if (shouldBeSticky) {
            sidebarWrapper.classList.remove('sidebar-not-sticky');
            sidebarWrapper.style.position = '';
            sidebarWrapper.style.top = '';
          } else {
            sidebarWrapper.classList.add('sidebar-not-sticky');
            sidebarWrapper.style.position = 'relative';
            sidebarWrapper.style.top = 'auto';
          }
        };
        
        // Throttle scroll handler for performance
        let ticking = false;
        window.addEventListener('scroll', () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              handleSidebarScroll();
              ticking = false;
            });
            ticking = true;
          }
        }, { passive: true });
        
        // Initial check
        handleSidebarScroll();
      }
    }
  }
  
  // Open sidebar
  if (sidebarToggle) {
    sidebarToggle.addEventListener('click', () => {
      body.classList.add('labratgifts-sidebar-open');
      sidebarToggle.setAttribute('aria-expanded', 'true');
    });
  }
  
  // Left-side toggle button
  const sidebarToggleLeft = document.querySelector('.labratgifts-sidebar-toggle-left');
  if (sidebarToggleLeft) {
    sidebarToggleLeft.addEventListener('click', () => {
      body.classList.add('labratgifts-sidebar-open');
      sidebarToggleLeft.setAttribute('aria-expanded', 'true');
    });
  }
  
  // Close sidebar
  const closeSidebar = () => {
    body.classList.remove('labratgifts-sidebar-open');
    if (sidebarToggle) {
      sidebarToggle.setAttribute('aria-expanded', 'false');
    }
    if (sidebarToggleLeft) {
      sidebarToggleLeft.setAttribute('aria-expanded', 'false');
    }
  };
  
  if (sidebarClose) {
    sidebarClose.addEventListener('click', closeSidebar);
  }
  
  if (sidebarOverlay) {
    sidebarOverlay.addEventListener('click', closeSidebar);
  }
  
  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && body.classList.contains('labratgifts-sidebar-open')) {
      closeSidebar();
    }
  });
  
  // ============================================
  // SIDEBAR MENU - Hierarchical Navigation Toggle
  // ============================================
  
  // Initialize: Auto-expand active menu paths on page load
  const activeMenuItems = document.querySelectorAll('.labratgifts-sidebar-menu__item--active');
  
  activeMenuItems.forEach(activeItem => {
    // Check if this active item has children (submenu)
    const hasChildren = activeItem.hasAttribute('data-has-children');
    
    if (hasChildren) {
      // Add runtime active class to expand the submenu
      activeItem.classList.add('active');
      
      // Find and update the toggle button ARIA state
      const toggleButton = activeItem.querySelector('.labratgifts-sidebar-menu__toggle');
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true');
      }
      
      // Find and show the submenu
      const submenu = activeItem.querySelector('.labratgifts-sidebar-menu');
      if (submenu) {
        submenu.setAttribute('aria-hidden', 'false');
      }
    }
    
    // Also check if this item is INSIDE a submenu - open parent path
    let parentItem = activeItem.closest('.labratgifts-sidebar-submenu-container')?.closest('.labratgifts-sidebar-menu__item');
    
    while (parentItem) {
      // Open this parent
      parentItem.classList.add('active');
      
      const parentToggle = parentItem.querySelector(':scope > .labratgifts-sidebar-menu__wrapper > .labratgifts-sidebar-menu__toggle');
      if (parentToggle) {
        parentToggle.setAttribute('aria-expanded', 'true');
      }
      
      const parentSubmenu = parentItem.querySelector(':scope > .labratgifts-sidebar-submenu-container > .labratgifts-sidebar-menu');
      if (parentSubmenu) {
        parentSubmenu.setAttribute('aria-hidden', 'false');
      }
      
      // Move up to check if there's a grandparent
      parentItem = parentItem.closest('.labratgifts-sidebar-submenu-container')?.closest('.labratgifts-sidebar-menu__item');
    }
  });
  
  const menuToggleButtons = document.querySelectorAll('.labratgifts-sidebar-menu__toggle');
  
  menuToggleButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const menuItem = button.closest('.labratgifts-sidebar-menu__item');
      const submenuContainer = menuItem.querySelector('.labratgifts-sidebar-submenu-container');
      const submenu = submenuContainer?.querySelector('.labratgifts-sidebar-menu');
      
      console.log('Sidebar menu toggle clicked:', {
        menuItem: menuItem,
        hasSubmenu: !!submenu,
        hasContainer: !!submenuContainer
      });
      
      if (!menuItem || !submenu) {
        console.log('Missing menuItem or submenu, returning');
        return;
      }
      
      // Get current state
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const isActive = menuItem.classList.contains('active');
      
      // Close all other submenus at the same level
      const parentMenu = menuItem.closest('.labratgifts-sidebar-menu');
      const siblingItems = parentMenu.querySelectorAll(':scope > .labratgifts-sidebar-menu__item');
      
      siblingItems.forEach(item => {
        if (item !== menuItem && item.classList.contains('active')) {
          item.classList.remove('active');
          const siblingButton = item.querySelector('.labratgifts-sidebar-menu__toggle');
          const siblingSubmenu = item.querySelector('.labratgifts-sidebar-menu');
          
          if (siblingButton) {
            siblingButton.setAttribute('aria-expanded', 'false');
          }
          if (siblingSubmenu) {
            siblingSubmenu.setAttribute('aria-hidden', 'true');
          }
        }
      });
      
      // Toggle current menu item
      if (isActive) {
        // Close
        console.log('Closing menu item');
        menuItem.classList.remove('active');
        button.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
      } else {
        // Open
        console.log('Opening menu item, adding active class');
        menuItem.classList.add('active');
        button.setAttribute('aria-expanded', 'true');
        submenu.setAttribute('aria-hidden', 'false');
        
        console.log('Menu item classes after adding active:', menuItem.className);
        
        // Smooth scroll the context heading into view
        setTimeout(() => {
          const contextHeading = submenuContainer.querySelector('.labratgifts-sidebar-menu__context');
          console.log('Context heading found:', contextHeading);
          if (contextHeading) {
            console.log('Context heading display:', window.getComputedStyle(contextHeading).display);
            contextHeading.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    });
    
    // Keyboard support
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        button.click();
      }
    });
  });
  
  // ============================================
  // SIDEBAR LIST - Collection List Toggle
  // ============================================
  const listToggleButtons = document.querySelectorAll('.labratgifts-sidebar-list__toggle');
  
  listToggleButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const listItem = button.closest('.labratgifts-sidebar-list__item');
      const nestedList = listItem.querySelector('.labratgifts-sidebar-list--nested');
      
      if (!nestedList) return;
      
      // Get current state
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      
      // Toggle state
      if (isExpanded) {
        button.setAttribute('aria-expanded', 'false');
        listItem.classList.remove('active');
      } else {
        button.setAttribute('aria-expanded', 'true');
        listItem.classList.add('active');
      }
    });
    
    // Keyboard support
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        button.click();
      }
    });
  });
  
  // ============================================
  // EXIT INTENT POPUP
  // Dynamic popup showing recently viewed or top-selling products
  // ============================================
  const exitPopup = document.querySelector('[data-exit-popup]');
  
  if (exitPopup) {
    const STORAGE_KEY_RECENTLY_VIEWED = 'labratgifts_recently_viewed';
    const STORAGE_KEY_POPUP_DISMISSED = 'labratgifts_exit_popup_dismissed';
    
    const dwellTime = parseInt(exitPopup.dataset.dwellTime) * 1000 || 10000;
    const frequencyHours = parseInt(exitPopup.dataset.frequencyHours) || 24;
    const mobileTrigger = exitPopup.dataset.mobileTrigger || 'scroll';
    const mobileScrollThreshold = parseInt(exitPopup.dataset.mobileScrollThreshold) || 50;
    const mobileInactivityTime = parseInt(exitPopup.dataset.mobileInactivityTime) * 1000 || 60000;
    
    const productsContainer = document.getElementById('exit-popup-products');
    const fallbackTemplate = document.getElementById('exit-popup-fallback-products');
    const titleRecent = exitPopup.querySelector('.labratgifts-exit-popup__title--recent');
    const titleFallback = exitPopup.querySelector('.labratgifts-exit-popup__title--fallback');
    const subtitleRecent = exitPopup.querySelector('.labratgifts-exit-popup__subtitle--recent');
    const subtitleFallback = exitPopup.querySelector('.labratgifts-exit-popup__subtitle--fallback');
    const closeButtons = exitPopup.querySelectorAll('[data-exit-popup-close]');
    
    let hasTriggered = false;
    let pageLoadTime = Date.now();
    let maxScrollReached = 0;
    let inactivityTimer = null;
    
    // Check if popup was recently dismissed
    function isPopupSuppressed() {
      try {
        const dismissed = localStorage.getItem(STORAGE_KEY_POPUP_DISMISSED);
        if (!dismissed) return false;
        
        const dismissedTime = parseInt(dismissed);
        const suppressUntil = dismissedTime + (frequencyHours * 60 * 60 * 1000);
        return Date.now() < suppressUntil;
      } catch (e) {
        return false;
      }
    }
    
    // Check if user has met dwell time requirement
    function hasDwellTimePassed() {
      return (Date.now() - pageLoadTime) >= dwellTime;
    }
    
    // Check if device is mobile (based on screen size only, not touch capability)
    // Touch detection was causing false positives on touch-enabled laptops
    function isMobile() {
      // Use only screen width to determine mobile - more reliable than touch detection
      // Many modern laptops have touch screens but should use exit intent (mouseleave)
      return window.matchMedia('(max-width: 768px)').matches;
    }
    
    // Get recently viewed products from localStorage
    function getRecentlyViewed() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY_RECENTLY_VIEWED);
        if (!stored) return [];
        
        const products = JSON.parse(stored);
        if (!Array.isArray(products)) return [];
        
        // Filter valid products
        return products.filter(p => p && p.id && p.handle && p.title && p.url && p.image);
      } catch (e) {
        console.warn('Error reading recently viewed products:', e);
        return [];
      }
    }
    
    // Format price from cents
    function formatMoney(cents) {
      if (!cents || cents === 0) return '$0.00';
      return `$${(cents / 100).toFixed(2)}`;
    }
    
    // Get campaign settings from exit popup data attributes or global window object
    function getCampaignSettings() {
      // Campaign settings should be exposed in window.campaignSettings by the theme
      return window.campaignSettings || {
        enabled: false,
        name: 'SALE',
        defaultDiscount: 10,
        applyToAll: false,
        collections: {}
      };
    }
    
    // Render product card HTML for recently viewed with campaign pricing
    function renderRecentProduct(product) {
      const hasComparePrice = product.comparePriceCents && product.comparePriceCents > product.priceCents;
      const originalPriceFormatted = formatMoney(product.priceCents);
      const comparePriceFormatted = hasComparePrice ? formatMoney(product.comparePriceCents) : '';
      
      // Check for campaign pricing
      const campaign = getCampaignSettings();
      let useCampaignPricing = false;
      let campaignDiscount = 0;
      let campaignPriceCents = product.priceCents;
      
      if (campaign.enabled) {
        // Use collectionPriority array to match Liquid's priority order
        // This ensures exit intent uses same discount as product page
        if (product.collections && Array.isArray(product.collections) && campaign.collectionPriority) {
          for (const campaignColl of campaign.collectionPriority) {
            if (campaignColl && campaignColl.handle && product.collections.includes(campaignColl.handle)) {
              campaignDiscount = campaignColl.discount;
              useCampaignPricing = true;
              break;
            }
          }
        }
        // Fallback to old method if collectionPriority not available
        if (!useCampaignPricing && product.collections && Array.isArray(product.collections)) {
          for (const collHandle of product.collections) {
            if (campaign.collections && campaign.collections[collHandle] !== undefined) {
              campaignDiscount = campaign.collections[collHandle];
              useCampaignPricing = true;
              break;
            }
          }
        }
        // Apply default discount if apply to all is enabled
        if (!useCampaignPricing && campaign.applyToAll) {
          campaignDiscount = campaign.defaultDiscount;
          useCampaignPricing = true;
        }
        
        if (useCampaignPricing && campaignDiscount > 0) {
          campaignPriceCents = Math.round(product.priceCents * (100 - campaignDiscount) / 100);
        }
      }
      
      const campaignPriceFormatted = formatMoney(campaignPriceCents);
      
      // Sold out badge only - no sale badge since pricing shows discount
      let badge = '';
      if (!product.available) {
        badge = '<span class="labratgifts-exit-popup__product-badge labratgifts-exit-popup__product-badge--soldout">Sold Out</span>';
      }
      
      // Build price HTML based on pricing type
      let priceHTML = '';
      if (useCampaignPricing && campaignDiscount > 0) {
        // Campaign pricing: WAS (original) / REG (compare) → NOW (campaign) + SAVE badge (only if discount > 0)
        priceHTML = `
          <div class="labratgifts-exit-popup__campaign-badge">
            <span class="labratgifts-badge labratgifts-badge--campaign">${campaign.name}</span>
          </div>
          <div class="labratgifts-exit-popup__price-row labratgifts-exit-popup__price-row--old">
            <span class="labratgifts-exit-popup__price-label">WAS</span>
            <span class="labratgifts-exit-popup__price labratgifts-exit-popup__price--was">${originalPriceFormatted}</span>
            ${hasComparePrice ? `
              <span class="labratgifts-exit-popup__price-label">REG</span>
              <span class="labratgifts-exit-popup__price labratgifts-exit-popup__price--reg">${comparePriceFormatted}</span>
            ` : ''}
          </div>
          <div class="labratgifts-exit-popup__price-row labratgifts-exit-popup__price-row--now">
            <span class="labratgifts-exit-popup__price-label labratgifts-exit-popup__price-label--now">NOW</span>
            <span class="labratgifts-exit-popup__price labratgifts-exit-popup__price--campaign">${campaignPriceFormatted}</span>
            <span class="labratgifts-exit-popup__save-badge">SAVE ${campaignDiscount}%</span>
          </div>
        `;
      } else if (hasComparePrice) {
        // Regular sale pricing (no campaign): WAS (compare price - original MSRP) → NOW (variant price - current sale)
        const savingsPercent = Math.round(((product.comparePriceCents - product.priceCents) / product.comparePriceCents) * 100);
        priceHTML = `
          <div class="labratgifts-exit-popup__price-row labratgifts-exit-popup__price-row--old">
            <span class="labratgifts-exit-popup__price-label">WAS</span>
            <span class="labratgifts-exit-popup__price labratgifts-exit-popup__price--was">${comparePriceFormatted}</span>
          </div>
          <div class="labratgifts-exit-popup__price-row labratgifts-exit-popup__price-row--now">
            <span class="labratgifts-exit-popup__price-label labratgifts-exit-popup__price-label--now">NOW</span>
            <span class="labratgifts-exit-popup__price labratgifts-exit-popup__price--sale">${originalPriceFormatted}</span>
            <span class="labratgifts-exit-popup__save-badge">SAVE ${savingsPercent}%</span>
          </div>
        `;
      } else {
        // Regular price (no sale)
        priceHTML = `<span class="labratgifts-exit-popup__price">${originalPriceFormatted}</span>`;
      }
      
      // Quick Add button - only for available products with single variant
      const canQuickAdd = product.available && product.variantId && !product.hasMultipleVariants;
      const quickAddButton = product.available 
        ? (canQuickAdd
            ? `<button type="button" class="labratgifts-exit-popup__quick-add" 
                       data-variant-id="${product.variantId}" 
                       data-product-title="${product.title.replace(/"/g, '&quot;')}"
                       aria-label="Add ${product.title} to cart">
                 <span class="labratgifts-exit-popup__quick-add-text">Add to Cart</span>
                 <span class="labratgifts-exit-popup__quick-add-loading" style="display:none;">
                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                     <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
                     <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round">
                       <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                     </path>
                   </svg>
                 </span>
                 <span class="labratgifts-exit-popup__quick-add-success" style="display:none;">Added!</span>
               </button>`
            : `<a href="${product.url}" class="labratgifts-exit-popup__quick-add labratgifts-exit-popup__quick-add--options">
                 Select Options
               </a>`)
        : `<span class="labratgifts-exit-popup__sold-out-text">Sold Out</span>`;
      
      return `
        <div class="labratgifts-exit-popup__product-card">
          <a href="${product.url}" class="labratgifts-exit-popup__product-link">
            <div class="labratgifts-exit-popup__product-image">
              <img src="${product.image}" alt="${product.imageAlt || product.title}" loading="lazy">
              ${badge}
            </div>
            <div class="labratgifts-exit-popup__product-info">
              <h3 class="labratgifts-exit-popup__product-title">${product.title}</h3>
              <div class="labratgifts-exit-popup__product-price">
                ${priceHTML}
              </div>
            </div>
          </a>
          <div class="labratgifts-exit-popup__product-action">
            ${quickAddButton}
          </div>
        </div>
      `;
    }
    
    // Handle Quick Add to Cart click
    function handleQuickAdd(e) {
      const button = e.target.closest('.labratgifts-exit-popup__quick-add[data-variant-id]');
      if (!button) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const variantId = button.dataset.variantId;
      const productTitle = button.dataset.productTitle;
      
      if (!variantId) return;
      
      // Show loading state
      const textEl = button.querySelector('.labratgifts-exit-popup__quick-add-text');
      const loadingEl = button.querySelector('.labratgifts-exit-popup__quick-add-loading');
      const successEl = button.querySelector('.labratgifts-exit-popup__quick-add-success');
      
      button.disabled = true;
      if (textEl) textEl.style.display = 'none';
      if (loadingEl) loadingEl.style.display = 'inline-flex';
      
      // Add to cart via AJAX
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1
        })
      })
      .then(response => {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(async (data) => {
        // Show success state
        if (loadingEl) loadingEl.style.display = 'none';
        if (successEl) successEl.style.display = 'inline-flex';
        
        // CRITICAL: Fetch latest cart and update everything
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Update cart count immediately
          if (window.updateCartCount) {
            window.updateCartCount(cart.item_count);
          } else {
            // Fallback for cart count update
            const countEls = document.querySelectorAll('[data-cart-count], .labratgifts-cart-count');
            countEls.forEach(el => {
              el.textContent = cart.item_count;
              if (cart.item_count > 0) {
                el.style.display = '';
                el.classList.remove('labratgifts-cart-count--hidden');
              }
            });
          }
          
          // Refresh cart drawer content if it exists
          if (window.refreshCartDrawer) {
            await window.refreshCartDrawer(cart);
          }
          
          // Dispatch cart update event for other components
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (err) {
          console.error('Error updating cart UI:', err);
        }
        
        // Show toast notification
        if (window.showToast) {
          window.showToast(`${productTitle} added to cart!`, 'success');
        }
        
        // Reset button after delay
        setTimeout(() => {
          button.disabled = false;
          if (successEl) successEl.style.display = 'none';
          if (textEl) textEl.style.display = 'inline';
        }, 2000);
      })
      .catch(error => {
        console.error('Quick add error:', error);
        // Reset to original state
        button.disabled = false;
        if (loadingEl) loadingEl.style.display = 'none';
        if (textEl) textEl.style.display = 'inline';
        
        if (window.showToast) {
          window.showToast('Could not add to cart. Please try again.', 'error');
        }
      });
    }
    
    // Populate popup with products
    function populateProducts() {
      const recentProducts = getRecentlyViewed();
      const maxProducts = 3; // Limit to 3 for optimal card sizing
      
      if (recentProducts.length > 0) {
        // Show recently viewed products
        const productsToShow = recentProducts.slice(0, maxProducts);
        productsContainer.innerHTML = productsToShow.map(renderRecentProduct).join('');
        
        // Update titles to show "recently viewed" variant
        if (titleRecent) {
          titleRecent.style.display = 'inline';
          titleFallback.style.display = 'none';
        }
        if (subtitleRecent) {
          subtitleRecent.style.display = 'inline';
          subtitleFallback.style.display = 'none';
        }
        
        console.log('Exit Popup: Showing', productsToShow.length, 'recently viewed products');
      } else {
        // Show fallback products (server-rendered)
        if (fallbackTemplate && fallbackTemplate.innerHTML.trim()) {
          productsContainer.innerHTML = fallbackTemplate.innerHTML;
          console.log('Exit Popup: Showing fallback products');
        } else {
          // No products available
          productsContainer.innerHTML = '<p style="text-align: center; color: #666; grid-column: 1 / -1;">Check out our amazing products!</p>';
          console.log('Exit Popup: No products available');
        }
        
        // Keep fallback titles visible (already visible by default)
      }
    }
    
    // Show popup
    function showPopup() {
      if (hasTriggered || isPopupSuppressed()) {
        console.log('Exit Popup: Suppressed or already triggered');
        return;
      }
      
      if (!hasDwellTimePassed()) {
        console.log('Exit Popup: Dwell time not met');
        return;
      }
      
      hasTriggered = true;
      populateProducts();
      
      exitPopup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus the container first (it has tabindex="-1"), then the close button
      const container = exitPopup.querySelector('.labratgifts-exit-popup__container');
      const closeButton = exitPopup.querySelector('.labratgifts-exit-popup__close');
      if (container) {
        setTimeout(() => {
          container.focus();
          if (closeButton) closeButton.focus();
        }, 100);
      }
      
      console.log('Exit Popup: Shown');
    }
    
    // Hide popup
    function hidePopup() {
      exitPopup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Save dismissal time
      try {
        localStorage.setItem(STORAGE_KEY_POPUP_DISMISSED, Date.now().toString());
      } catch (e) {
        console.warn('Could not save popup dismissal:', e);
      }
      
      console.log('Exit Popup: Hidden and suppressed for', frequencyHours, 'hours');
    }
    
    // Desktop exit intent detection (mouse leaves viewport toward top)
    function handleMouseLeave(e) {
      if (e.clientY <= 0 && !hasTriggered && hasDwellTimePassed()) {
        showPopup();
      }
    }
    
    // Mobile: Track scroll depth
    function handleScroll() {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      maxScrollReached = Math.max(maxScrollReached, scrollPercent);
      
      // Reset inactivity timer on scroll
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      
      // If scroll threshold met, start inactivity timer
      if (maxScrollReached >= mobileScrollThreshold && !hasTriggered) {
        inactivityTimer = setTimeout(() => {
          if (!hasTriggered && hasDwellTimePassed()) {
            showPopup();
          }
        }, mobileInactivityTime);
      }
    }
    
    // Focus trap for accessibility
    function handleFocusTrap(e) {
      if (exitPopup.getAttribute('aria-hidden') !== 'false') return;
      
      const focusableElements = exitPopup.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
    
    // Initialize event listeners
    function init() {
      // Close button handlers
      closeButtons.forEach(btn => {
        btn.addEventListener('click', hidePopup);
      });
      
      // Quick Add to Cart handler (delegated for dynamically added buttons)
      exitPopup.addEventListener('click', handleQuickAdd);
      
      // Escape key to close and focus trap
      document.addEventListener('keydown', (e) => {
        if (exitPopup.getAttribute('aria-hidden') === 'false') {
          if (e.key === 'Escape') {
            hidePopup();
          }
          handleFocusTrap(e);
        }
      });
      
      // Set up trigger based on device type
      if (isMobile()) {
        if (mobileTrigger === 'disabled') {
          console.log('Exit Popup: Disabled on mobile');
          return;
        }
        
        if (mobileTrigger === 'scroll') {
          window.addEventListener('scroll', handleScroll, { passive: true });
        } else if (mobileTrigger === 'time') {
          // Time-based trigger for mobile
          setTimeout(() => {
            if (!hasTriggered) {
              showPopup();
            }
          }, mobileInactivityTime);
        }
        
        console.log('Exit Popup: Mobile mode -', mobileTrigger);
      } else {
        // Desktop: use mouseleave on document
        document.addEventListener('mouseleave', handleMouseLeave);
        console.log('Exit Popup: Desktop mode - exit intent enabled');
      }
    }
    
    // Don't trigger on checkout or cart pages
    const excludedPaths = ['/checkout', '/cart', '/account'];
    const currentPath = window.location.pathname;
    const isExcluded = excludedPaths.some(path => currentPath.startsWith(path));
    
    if (!isExcluded && !isPopupSuppressed()) {
      init();
      console.log('Exit Popup: Initialized');
    } else {
      console.log('Exit Popup: Skipped (excluded page or suppressed)');
    }
  }
});
